import { X, ExternalLink } from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import { Button } from "@/shared/ui/Button";
import { sanitizeHtml } from "@/shared/utils/html.utils";
import { LAST_SEEN_POPUP_KEY } from "../configs/constants";
import { useLatestPopup } from "../hooks/useLatestPopupQuery";
import type { Changelog } from "../types/changelog.types";

const ChangelogPopup = () => {
  const { changelog, isLoading } = useLatestPopup();
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    if (isLoading || !changelog) return;

    const lastSeenId = localStorage.getItem(LAST_SEEN_POPUP_KEY);
    if (lastSeenId === changelog.id) return;

    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
    if (new Date(changelog.created_at) < oneMonthAgo) return;

    setShouldRender(true);
    // Small delay for mount animation
    const timer = setTimeout(() => setIsVisible(true), 50);
    return () => clearTimeout(timer);
  }, [changelog, isLoading]);

  const handleClose = useCallback((popupChangelog: Changelog) => {
    setIsVisible(false);
    localStorage.setItem(LAST_SEEN_POPUP_KEY, popupChangelog.id);
    // Wait for animation to finish before unmounting
    setTimeout(() => setShouldRender(false), 300);
  }, []);

  // Close on Escape key
  useEffect(() => {
    if (!shouldRender || !changelog) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleClose(changelog);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [shouldRender, changelog, handleClose]);

  if (!shouldRender || !changelog) return null;

  const handleCtaClick = () => {
    if (changelog.popup_cta_link) {
      window.open(changelog.popup_cta_link, "_blank", "noopener,noreferrer");
    }
    handleClose(changelog);
  };

  return (
    <div
      className={`fixed bottom-4 right-4 z-50 w-[380px] max-w-[calc(100vw-2rem)] bg-white rounded-lg shadow-lg border transition-all duration-300 ease-out ${
        isVisible
          ? "translate-y-0 opacity-100"
          : "translate-y-4 opacity-0"
      }`}
      role="dialog"
      aria-label="Notification de nouveauté"
    >
      {/* Image */}
      {changelog.popup_image && (
        <img
          src={changelog.popup_image.large_url}
          alt=""
          className="w-full h-40 object-cover rounded-t-lg"
        />
      )}

      <div className="p-4">
        {/* Close button */}
        <button
          type="button"
          onClick={() => handleClose(changelog)}
          className="absolute top-2 right-2 p-1 rounded-full bg-white/80 hover:bg-white text-gray-500 hover:text-gray-700 transition-colors"
          aria-label="Fermer"
        >
          <X size={16} />
        </button>

        {/* Title */}
        {changelog.popup_title && (
          <h3 className="font-semibold text-base mb-2">
            {changelog.popup_title}
          </h3>
        )}

        {/* Text */}
        {changelog.popup_text && (
          <div
            className="prose prose-sm max-w-none line-clamp-4 text-muted-foreground"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(changelog.popup_text) }}
          />
        )}

        {/* CTA Button */}
        {changelog.popup_cta_link && (
          <Button
            variant="action"
            size="sm"
            onClick={handleCtaClick}
            className="mt-3 w-full flex items-center justify-center gap-2"
          >
            Découvrir
            <ExternalLink size={14} />
          </Button>
        )}
      </div>
    </div>
  );
};

export default ChangelogPopup;
